// @owners { team: patient-health }
// eslint-disable-next-line @alto/no-pocky-import
import { FlexRow, SimpleContainerHeader, Text, palette, shadows, spacing } from '@alto/pocky';
import autobind from 'autobind-decorator';
import { get } from 'lodash';
import React, { type ReactNode } from 'react';
import styled from 'styled-components';
import { type Medication } from '~shared/features/my-meds/types';
import { getDisplayName } from '~shared/helpers/helper';
import { type Transfer, type User } from '~shared/types/clients';
import CollapsibleSection from './CollapsibleSection';
import MedListAddMedicationButton from './MedListAddMedicationButtonContainer';
import { MedListRowContainer } from './MedListRow';
import MedListRow from './MedListRowContainer';
import MedListTransferRow from './MedListTransferRowContainer';

type Props = {
  readonly active: Medication[];
  readonly familyMembers: User[];
  readonly hasFamily: boolean;
  readonly initialRxNumber: string | null | undefined;
  readonly needsAttention: Medication[];
  readonly transfers: Transfer[];
  readonly currentUser: User;
};

export const MedListContainer = styled(FlexRow)`
  ${shadows.boxShadow1};
  border-radius: ${spacing.borderRadius};
  margin-bottom: ${spacing.lgSpacing};
`;

const MedListSectionTitleContainer = styled(FlexRow)`
  border: 1px solid ${palette.grey2};
  border-bottom: none;
`;

export const MedListAddButtonContainer = styled(FlexRow)`
  align-items: flex-end;
  background-color: ${palette.altoColorsW};
  border: 1px solid ${palette.grey2};
  padding: ${spacing.mdSpacing};
`;

// @ts-expect-error TS(7006): Parameter 'user' implicitly has an 'any' type.
const getTitle = (user) => `${getDisplayName(user)}'s Meds`;

export default class MedListActive extends React.PureComponent<Props> {
  static defaultProps = {
    initialRxNumber: null,
  };

  componentDidMount() {
    // ref is only set when rx number matches
    if (this.rowRef) {
      // scroll to the header of the collapsible that
      // matches the given RxNumber. 75 is to account
      // for the navbar height
      const navbarHeight = get(document.getElementsByClassName('patient-nav'), [0, 'offsetHeight'], 75);

      // @ts-expect-error TS(2339): Property 'offsetTop' does not exist on type 'never'.
      const scrollPosition = this.rowRef.offsetTop - navbarHeight;
      window.scrollTo(0, scrollPosition);
    }
  }

  rowRef = null;

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  setMedListRowRef(element: HTMLDivElement | null | undefined) {
    // @ts-expect-error TS(2322): Type 'HTMLDivElement | null | undefined' is not assignable to type 'null'.
    this.rowRef = element;
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  renderAddMedicationsButton() {
    return (
      <MedListAddButtonContainer>
        <MedListAddMedicationButton />
      </MedListAddButtonContainer>
    );
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  renderMedications(medications: Medication[], showUserTag = false): ReactNode[] {
    const { initialRxNumber } = this.props;

    return medications.map((medication) => {
      const match = medication?.prescriptions?.some?.((p) => p.rx_number === initialRxNumber) ?? false;

      return (
        <div
          // eslint-disable-next-line @typescript-eslint/unbound-method
          ref={match ? this.setMedListRowRef : null}
          key={medication.prescriptions[0].rx_number}
        >
          <MedListRow
            medication={medication}
            initShowDetail={match}
            showUserTag={showUserTag}
          />
        </div>
      );
    });
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  renderEmptyMedicationState(user: User | null | undefined = null) {
    const greeting = user ? `${getDisplayName(user)} doesn't` : "You don't";

    return (
      <>
        <MedListRowContainer short>
          <Text>
            {greeting} have any active medications. We&#39;ll let you know if we receive any new prescriptions.
          </Text>
        </MedListRowContainer>
        {this.renderAddMedicationsButton()}
      </>
    );
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  render() {
    const { active, familyMembers, hasFamily, needsAttention, transfers, currentUser } = this.props;
    return (
      <>
        {hasFamily ? (
          familyMembers.map((user) => {
            const medications = active.filter((medication) => user.id === medication.userID);
            const needsAttentionMedication = needsAttention.filter((medication) => user.id === medication.userID);
            const title = getTitle(user);

            if (!medications.length && !needsAttention.length) {
              return null;
            }

            return (
              <CollapsibleSection
                title={title}
                darkHeader
                key={user.id}
              >
                {medications.length > 0 || needsAttentionMedication.length > 0 ? (
                  <>
                    {this.renderMedications([...needsAttentionMedication, ...medications])}
                    {this.renderAddMedicationsButton()}
                  </>
                ) : (
                  this.renderEmptyMedicationState(user)
                )}
              </CollapsibleSection>
            );
          })
        ) : (
          <MedListContainer>
            {active.length > 0 || needsAttention.length > 0 ? (
              <>
                <MedListSectionTitleContainer>
                  <SimpleContainerHeader>{getTitle(currentUser)}</SimpleContainerHeader>
                </MedListSectionTitleContainer>
                {this.renderMedications([...needsAttention, ...active])}
                {this.renderAddMedicationsButton()}
              </>
            ) : null}
            {!needsAttention.length && !active.length ? this.renderEmptyMedicationState() : null}
          </MedListContainer>
        )}

        {transfers.length > 0 ? (
          <MedListContainer>
            <MedListSectionTitleContainer>
              <SimpleContainerHeader>Pending Transfers</SimpleContainerHeader>
            </MedListSectionTitleContainer>
            <MedListRowContainer
              spacing={spacing.xsSpacing}
              short
            >
              <Text>
                We&#39;re processing your transfer requests. You&#39;ll be notified as soon as they&#39;re ready to be
                scheduled.
              </Text>

              {transfers.map((transfer) => (
                <MedListTransferRow
                  key={transfer.id}
                  transfer={transfer}
                />
              ))}
            </MedListRowContainer>
          </MedListContainer>
        ) : null}
      </>
    );
  }
}
